import React, { useEffect, useState } from 'react';
import { Alert, Card, Col, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faSignal, faMicrochip, faMemory, faStopwatch } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

export default function OperationalHealthPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let disposed = false;
    let timer;
    let controller;
    let pending = false;
    const load = async () => {
      clearTimeout(timer);
      if (disposed || document.hidden || pending) return;
      pending = true;
      controller = new AbortController();
      try {
        const response = await axios.get('/api/admin/dashboard/operational', { signal: controller.signal, timeout: 15000 });
        if (!disposed) {
          setData(response.data);
          setError(Boolean(response.data.unavailable));
        }
      } catch (err) {
        if (!disposed && err.code !== 'ERR_CANCELED') setError(true);
      } finally {
        pending = false;
        if (!disposed && !document.hidden) timer = setTimeout(load, 30000);
      }
    };
    const visibility = () => {
      clearTimeout(timer);
      if (!document.hidden) load();
    };
    load();
    document.addEventListener('visibilitychange', visibility);
    return () => {
      disposed = true;
      clearTimeout(timer);
      controller?.abort();
      document.removeEventListener('visibilitychange', visibility);
    };
  }, []);
  const percent = value => value == null ? '—' : `${value.toLocaleString('pt-BR')}%`;
  const cards = [
    [faUsers, 'Usuários ativos', data?.active_users?.toLocaleString('pt-BR') ?? '—', 'Funcionários não arquivados e sem bloqueio de redefinição de senha.'],
    [faSignal, 'Online recentemente', data?.online_users?.toLocaleString('pt-BR') ?? '—', 'Usuários ativos com token válido utilizado nos últimos 5 minutos.'],
    [faMicrochip, 'CPU do host Linux', percent(data?.cpu_percent), 'Média entre leituras. A primeira leitura precisa de mais um ciclo.'],
    [faMemory, 'Memória do host Linux', percent(data?.memory_percent), 'Memória ocupada, descontando a memória disponível ao sistema.'],
    [faStopwatch, 'Resposta média da API', data?.response_mean_ms == null ? '—' : `${data.response_mean_ms.toLocaleString('pt-BR')} ms`,
      data?.response_sampling_enabled ? `Amostra de 1% das requisições nos últimos 5 minutos (${data?.sample_count ?? 0} amostras).` : 'Coleta opcional desativada para preservar o desempenho.'],
  ];
  return (
    <section aria-label="Saúde operacional" className="mb-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h2 className="h5 mb-0">Saúde operacional</h2>
        <small className="text-muted">{!data && !error ? <Spinner size="sm" aria-label="Carregando métricas" /> : data?.observed_at ? `Leitura: ${new Date(data.observed_at).toLocaleTimeString('pt-BR')}` : 'Aguardando leitura'} · atualização a cada 30 s</small>
      </div>
      {error && <Alert variant="warning">Métricas indisponíveis. Os valores anteriores, quando exibidos, podem estar desatualizados.</Alert>}
      <Row className="g-3">
        {cards.map(([icon, title, value, description]) => (
          <Col xs={12} sm={6} xl key={title}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <FontAwesomeIcon icon={icon} className="text-primary mb-3" size="lg" aria-hidden="true" />
                <div className="text-muted small">{title}</div>
                <div className="h3 my-2">{value}</div>
                <small className="text-muted">{description}</small>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      <small className="text-muted d-block mt-2">Online é uma estimativa de atividade, incluindo atualizações automáticas. CPU e memória são do host visível ao container, não apenas do PHP. O tempo da API não inclui a fila anterior ao PHP nem a rede do usuário.</small>
    </section>
  );
}
